// Centralised audio & voice helpers for the workout session player.
// Uses Web Audio API for beeps (no external files) and SpeechSynthesis for voice.

let _italianVoice = null;

function pickItalianVoice() {
  if (_italianVoice) return _italianVoice;
  const voices = window.speechSynthesis?.getVoices?.() || [];
  _italianVoice =
    voices.find((v) => v.lang?.startsWith("it")) ||
    voices.find((v) => v.name?.toLowerCase().includes("italian")) ||
    null;
  return _italianVoice;
}

// Ensure voices are loaded (chromium fires this event async)
if (typeof window !== "undefined" && window.speechSynthesis) {
  window.speechSynthesis.onvoiceschanged = () => {
    _italianVoice = null;
    pickItalianVoice();
  };
  pickItalianVoice();
}

export function speak(text, { rate = 1.05, pitch = 1.0, volume = 1.0 } = {}) {
  try {
    if (!window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "it-IT";
    u.rate = rate;
    u.pitch = pitch;
    u.volume = volume;
    const v = pickItalianVoice();
    if (v) u.voice = v;
    // Cancel any pending speech to keep it snappy
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch (e) {
    // ignore
  }
}

export function playBeep({ freq = 880, duration = 0.18, type = "sine", volume = 0.35 } = {}) {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration);
    osc.onended = () => ctx.close();
  } catch (e) {
    // ignore
  }
}

// Named cues for readability
export const cues = {
  countdownTick: () => playBeep({ freq: 540, duration: 0.08, type: "square", volume: 0.22 }),
  restEnd: () => {
    playBeep({ freq: 660, duration: 0.12, volume: 0.3 });
    setTimeout(() => playBeep({ freq: 990, duration: 0.35, volume: 0.4 }), 140);
  },
  setComplete: () => {
    playBeep({ freq: 880, duration: 0.1, volume: 0.28 });
    setTimeout(() => playBeep({ freq: 1180, duration: 0.15, volume: 0.32 }), 80);
  },
  sessionStart: () => {
    playBeep({ freq: 440, duration: 0.14, volume: 0.3 });
    setTimeout(() => playBeep({ freq: 660, duration: 0.14, volume: 0.32 }), 150);
    setTimeout(() => playBeep({ freq: 880, duration: 0.3, volume: 0.38 }), 300);
  },
  sessionComplete: () => {
    // C major arpeggio → E → G → C — "victory"
    const notes = [523, 659, 784, 1046];
    notes.forEach((f, i) => setTimeout(() => playBeep({ freq: f, duration: 0.22, volume: 0.34 }), i * 180));
  },
};

// Compound cues that combine sound + voice
export function announceNextExercise(name) {
  cues.setComplete();
  setTimeout(() => speak(`Prossimo. ${name}`), 200);
}

export function announceRestEnd() {
  cues.restEnd();
  setTimeout(() => speak("Via!"), 380);
}

export function announceSessionStart(exerciseName) {
  cues.sessionStart();
  setTimeout(() => speak(`Iniziamo. Primo esercizio: ${exerciseName}`), 500);
}

export function announceSessionComplete() {
  cues.sessionComplete();
  setTimeout(() => speak("Sessione completata. Ottimo lavoro!"), 700);
}
